/**
 * The challenge configurator (ADR-0033) — upcomers' structure in our editorial language: product and evaluation, account
 * size, challenge type, model, then the rules of exactly that configuration; the summary with the price and the action
 * sits beside it on a wide screen and is pinned to the bottom on a phone. Used by /programs and the landing's desk.
 */
import { cn } from "@prop/ui";
import { useConfigT } from "../../../i18n";
import type { Evaluation, Product } from "./model";
import { EvaluationRow, Field, ModelSwitch, ProductRow, SizeField, TypeCards } from "./Pickers";
import { Rise } from "./motion";
import { RulesPanel } from "./RulesPanel";
import { StickyBar, SummaryCard } from "./Summary";
import { useConfigurator } from "./useConfigurator";

/** Bottom of the pinned phone bar: above the app's tab bar (< 1280px), or the viewport edge on the landing. */
export const ABOVE_TABS = "bottom-[calc(3.5rem+1px+env(safe-area-inset-bottom))] xl:bottom-0";

export function Configurator({ initial, stickyBottom = ABOVE_TABS, summaryTop = "lg:top-20", className }: { initial?: { product?: Product; evaluation?: Evaluation }; stickyBottom?: string; summaryTop?: string; className?: string }) {
  const t = useConfigT();
  const c = useConfigurator(initial);
  if (c.loading) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  if (!c.products.length) return <p className="text-sm text-muted-foreground">{t("empty")}</p>;
  return (
    <section aria-label={t("label")} className={cn("grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]", className)}>
      <div className="flex min-w-0 flex-col gap-4">
        <Rise className="edge-glow flex min-w-0 flex-col gap-5 rounded-[14px] bg-card p-4 sm:p-5">
          {/* Product and evaluation share one row on a wide screen, as on upcomers. */}
          <div className="grid min-w-0 gap-5 2xl:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]">
            <Field label={t("product.label")}>
              <ProductRow c={c} />
            </Field>
            <Field label={t("evaluation.label")}>
              <EvaluationRow c={c} />
            </Field>
          </div>
          <SizeField c={c} />
          <Field label={t("type.label")}>
            <TypeCards c={c} />
          </Field>
          <ModelSwitch c={c} />
        </Rise>
        <Rise className="edge-glow rounded-[14px] bg-card p-4 sm:p-5">
          <RulesPanel c={c} />
        </Rise>
      </div>
      <Rise delay={0.12} className={cn("flex min-w-0 flex-col gap-4 lg:sticky", summaryTop)}>
        <SummaryCard c={c} />
      </Rise>
      <StickyBar c={c} bottom={stickyBottom} />
    </section>
  );
}
